import type ApiRequestError from 'src/core/models/api/ApiRequestError';
import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import Constants from 'src/core/utils/Constants';
import type AnalyticsEventParams from 'src/core/models/AnalyticsEventParams';
import type AnalyticsUser from 'src/core/models/AnalyticsUser';
import type AnalyticsUserProperties from 'src/core/models/AnalyticsUserProperties';
import {
	AnalyticsService,
	CrashlyticsService,
	FacebookService,
	LinkingService,
	TranslationService,
	ValidationService,
} from 'src/data/services/Services';

export const useServicesStore = create(
	combine({}, () => {
		const sendToAnalytics = (message: string, send: () => Promise<void>): void => {
			try {
				send().catch((error: ApiRequestError) => {
					CrashlyticsService.captureEvent({ message, extra: { error } });
				});
			} catch (error: unknown) {
				CrashlyticsService.captureEvent({ message, extra: { error } });
			}
		};

		const logEvent = ({
			name,
			params,
			user,
		}: {
			name: string;
			params: AnalyticsEventParams;
			user: AnalyticsUser | null;
		}): void =>
			sendToAnalytics(`Sending the analytics event ${name} failed.`, () =>
				AnalyticsService.sendEvent({ name, params, user }),
			);

		const logScreenView = ({ screenName, user }: { screenName: string; user: AnalyticsUser | null }): void =>
			sendToAnalytics(`Sending the analytics event ${Constants.analyticsEventScreenView} failed.`, () =>
				AnalyticsService.sendScreenView({ screenName, user }),
			);

		const setAnalyticsUserProperties = (properties: AnalyticsUserProperties): void =>
			sendToAnalytics('Setting the analytics user properties failed.', () =>
				AnalyticsService.setUserProperties(properties),
			);

		const setAnalyticsCollectionEnabled = (enabled: boolean) => AnalyticsService.setCollectionEnabled(enabled);

		const initializeFacebook = () => FacebookService.initialize();

		const captureEvent = (options: { message: string; extra?: Record<string, unknown> }) =>
			CrashlyticsService.captureEvent(options);

		const setCrashlyticsUser = (options: { id?: number | string }) => CrashlyticsService.setUser(options);

		const clearCrashlyticsUser = () => CrashlyticsService.clearUser();

		const getI18n = () => TranslationService.i18n;

		const openUrlExternally = ({ url }: { url: string }) => LinkingService.openUrlExternally({ url });

		const isValidEmail = ({ email }: { email: string }) => ValidationService.isValidEmail(email);

		return {
			logEvent,
			logScreenView,
			setAnalyticsUserProperties,
			setAnalyticsCollectionEnabled,
			initializeFacebook,
			captureEvent,
			setCrashlyticsUser,
			clearCrashlyticsUser,
			getI18n,
			openUrlExternally,
			isValidEmail,
		};
	}),
);
